import React, { useState, useEffect } from 'react';
import { TextField, Button, Link, Typography, Container, Box, FormControlLabel, Checkbox, Card, CardContent } from '@mui/material';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import ChangePasswordDialog from '../components/ChangePasswordDialog';
import { isTokenExpired } from '../utils/authUtils';
import { useUser } from '../context/UserContext';
import { useOrg } from '../context/OrgContext';
import { clearOwnerUnlock } from '../utils/ownerSession';
import { setRememberMe as setRememberMePref, touchActivity } from '../utils/idleLogout';

const Login = () => {
  const navigate = useNavigate();
  const { name: companyName, logoUrl, isPlatform } = useOrg();
  // Signed out for inactivity (utils/idleLogout.js): say so once
  useEffect(() => {
    try {
      if (sessionStorage.getItem('wk_logout_reason') === 'idle') {
        sessionStorage.removeItem('wk_logout_reason');
        setLoginError('You were signed out after 30 minutes of inactivity. Please log in again.');
      }
    } catch (e) { /* storage unavailable */ }
  }, []);
  const { refreshUser } = useUser();
  const [formData, setFormData] = useState({ username: '', password: '' });
  const [rememberMe, setRememberMe] = useState(false);
  const [errors, setErrors] = useState({});
  const [loginError, setLoginError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  // 2FA step: set when the server answers requiresTwoFactor
  const [twoFactor, setTwoFactor] = useState(null); // { tempToken, method, destination }
  const [twoFactorCode, setTwoFactorCode] = useState('');
  // The same login exists at more than one company: the server lists them and the
  // user picks one, then the login is retried pinned to that company
  const [chooser, setChooser] = useState(null); // [{ id, slug, name, logoUrl }]

  // Check if user is already logged in
  useEffect(() => {
    // console.log('Login useEffect: Checking for existing token'); // Removed log
    
    // Check localStorage first
    let token = localStorage.getItem('token');
    let userJson = localStorage.getItem('user');
    
    // If not in localStorage, check sessionStorage
    if (!token) {
      token = sessionStorage.getItem('backup_token');
      userJson = sessionStorage.getItem('backup_user');
      // console.log('Login useEffect: Backup token exists in sessionStorage?', !!token); // Removed log
    } else {
      // console.log('Login useEffect: Token exists in localStorage'); // Removed log
    }
    
    if (token) {
      // Check if token is valid before redirecting
      if (!isTokenExpired(token)) {
        // console.log('Login useEffect: Token is valid, redirecting to dashboard'); // Removed log
        
        // If token was found in sessionStorage but not localStorage, restore it
        if (!localStorage.getItem('token') && sessionStorage.getItem('backup_token')) {
          localStorage.setItem('token', sessionStorage.getItem('backup_token'));
          
          if (userJson) {
            localStorage.setItem('user', userJson);
          }
        }
        
        navigate('/dashboard');
      } else {
        // console.log('Login useEffect: Token is expired, clearing storage'); // Removed log
        // Clear invalid token from both storage locations
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        sessionStorage.removeItem('backup_token');
        sessionStorage.removeItem('backup_user');
        setLoginError('Your session has expired. Please log in again.');
      }
    } else {
      // console.log('Login useEffect: No token found in either storage location'); // Removed log
    }
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({ ...prevData, [name]: value }));
  };

  const validateForm = () => {
    const { username, password } = formData;
    const newErrors = {};
    if (!username) newErrors.username = 'Username is required.';
    if (!password) newErrors.password = 'Password is required.';
    return newErrors;
  };

  // Store token + user data and route into the app (shared by password and
  // 2FA login completions)
  const enterDashboard = async (data) => {
    await refreshUser();
    if (typeof window.refreshUserFromJobData === 'function') {
      window.refreshUserFromJobData();
    }
    // A partner owner whose company is still being set up lands in the setup wizard
    if (data?.primaryRole === 'OWp' && data?.orgKind === 'partner') {
      try {
        const { data: me } = await axios.get(`${process.env.REACT_APP_API_URL || ''}/api/org/me`, { headers: { Authorization: `Bearer ${data.token}` } });
        if (!me?.setup?.completedAt) { navigate('/setup/wizard'); return; }
      } catch (e) {
        // fall through to the dashboard
      }
    }
    navigate('/dashboard');
  };

  const completeLogin = async (data) => {
    // A new sign-in never inherits the previous user's Owner Console unlock in this tab
    clearOwnerUnlock();
    // Without "Remember me" the session ends after 30 minutes of inactivity (utils/idleLogout.js)
    setRememberMePref(rememberMe);
    touchActivity();
    try {
      localStorage.setItem('token', data.token);

      const userData = {
        _id: data._id,
        username: data.username,
        email: data.email,
        // Store both primaryRole and legacy role for backward compatibility
        primaryRole: data.primaryRole || data.role,
        role: data.role || data.primaryRole,
        // Ensure secondaryRoles is always stored as an array
        secondaryRoles: Array.isArray(data.secondaryRoles)
          ? data.secondaryRoles
          : (data.secondaryRoles && typeof data.secondaryRoles === 'object')
            ? Object.keys(data.secondaryRoles).filter(role => data.secondaryRoles[role])
            : []
      };

      localStorage.setItem('user', JSON.stringify(userData));

      // Also store in sessionStorage as backup
      sessionStorage.setItem('backup_token', data.token);
      sessionStorage.setItem('backup_user', JSON.stringify(userData));
    } catch (storageError) {
      // Storage may be unavailable (private mode); login still proceeds
    }

    // Check if password change is required
    if (data.isFirstLogin) {
      setShowChangePasswordDialog(true);
    } else {
      await enterDashboard(data);
    }
  };

  const handleApiError = (error) => {
    if (error.response) {
      // Server responded with an error
      setLoginError(error.response.data.message || 'Login failed. Please try again.');
    } else if (error.request) {
      // Request was made but no response received
      setLoginError('Network error. Please check your connection and try again.');
    } else {
      // Something else happened while setting up the request
      setLoginError('An unexpected error occurred. Please try again.');
    }
  };

  // One login attempt; `orgId` is set on the retry after the company chooser
  const performLogin = async (orgId) => {
    setIsLoading(true);
    setLoginError('');
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/login`, {
        username: formData.username,
        password: formData.password,
        rememberMe: rememberMe,
        ...(orgId ? { orgId } : {})
      });

      if (response.data.chooseOrganization) {
        // The same login exists at several companies: ask which one
        setChooser(response.data.chooseOrganization);
      } else if (response.data.requiresTwoFactor) {
        // Second step: prompt for the verification code
        setChooser(null);
        setTwoFactor({
          tempToken: response.data.tempToken,
          method: response.data.twoFactorMethod,
          destination: response.data.destination
        });
      } else {
        setChooser(null);
        await completeLogin(response.data);
      }
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const newErrors = validateForm();
    setErrors(newErrors);

    if (Object.keys(newErrors).length === 0) {
      await performLogin();
    }
  };

  const handleTwoFactorSubmit = async (event) => {
    event.preventDefault();
    if (!twoFactorCode.trim()) {
      setLoginError('Please enter your verification code.');
      return;
    }

    setIsLoading(true);
    setLoginError('');

    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/auth/verify-2fa`, {
        code: twoFactorCode.trim(),
        tempToken: twoFactor.tempToken
      });
      await completeLogin(response.data);
    } catch (error) {
      handleApiError(error);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle successful password change
  const handlePasswordChangeSuccess = async () => {
    await enterDashboard();
  };

  return (
    <Container maxWidth="xs">
      <Card sx={{ mt: 8, p: 2 }}>
        <CardContent>
          {!isPlatform && logoUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
              <Box component="img" src={logoUrl} alt={companyName} sx={{ maxHeight: 72, maxWidth: 220, objectFit: 'contain' }} />
            </Box>
          )}
          <Typography variant="h4" align="center">
            {companyName}
          </Typography>
          <Typography variant="h6" align="center" gutterBottom>
            Provider Login
          </Typography>
          {twoFactor ? (
          <form onSubmit={handleTwoFactorSubmit}>
            <Typography variant="body2" sx={{ mt: 1 }}>
              {twoFactor.method === 'app'
                ? 'Enter the 6-digit code from your authenticator app.'
                : `A verification code was sent to ${twoFactor.destination || (twoFactor.method === 'sms' ? 'your phone' : 'your email')}.${twoFactor.method === 'email' ? " If you don't see it, check your spam folder." : ''}`}
            </Typography>
            <TextField
              fullWidth
              label="Verification Code"
              name="twoFactorCode"
              margin="normal"
              variant="outlined"
              autoFocus
              autoComplete="one-time-code"
              inputProps={{ inputMode: 'numeric', maxLength: 6 }}
              value={twoFactorCode}
              onChange={(e) => setTwoFactorCode(e.target.value)}
            />
            {loginError && (
              <Typography color="error" variant="body2">
                {loginError}
              </Typography>
            )}
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              sx={{ mt: 2 }}
              disabled={isLoading}
            >
              {isLoading ? 'Verifying...' : 'Verify'}
            </Button>
            <Button
              fullWidth
              sx={{ mt: 1 }}
              onClick={() => { setTwoFactor(null); setTwoFactorCode(''); setLoginError(''); }}
            >
              Back to Login
            </Button>
          </form>
          ) : chooser ? (
          <div>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>Which company are you signing in to?</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              This login exists at more than one company.
            </Typography>
            {chooser.map((org) => (
              <Button
                key={org.id}
                fullWidth
                variant="outlined"
                sx={{ mb: 1, justifyContent: 'flex-start', textTransform: 'none' }}
                disabled={isLoading}
                onClick={() => performLogin(org.id)}
                startIcon={org.logoUrl ? <img src={org.logoUrl} alt="" style={{ width: 24, height: 24, objectFit: 'contain' }} /> : null}
              >
                {org.name}
              </Button>
            ))}
            {loginError && (
              <Typography color="error" variant="body2">
                {loginError}
              </Typography>
            )}
            <Button fullWidth sx={{ mt: 1 }} onClick={() => { setChooser(null); setLoginError(''); }}>
              Back to Login
            </Button>
          </div>
          ) : (
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Username"
              name="username"
              margin="normal"
              variant="outlined"
              value={formData.username}
              onChange={handleChange}
              error={Boolean(errors.username)}
              helperText={errors.username}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              name="password"
              margin="normal"
              variant="outlined"
              value={formData.password}
              onChange={handleChange}
              error={Boolean(errors.password)}
              helperText={errors.password}
            />
            {loginError && (
              <Typography color="error" variant="body2">
                {loginError}
              </Typography>
            )}
            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                />
              }
              label="Remember me for 30 days"
              sx={{ mt: 1 }}
            />
            
            <Button
              fullWidth
              variant="contained"
              color="primary"
              type="submit"
              sx={{ mt: 2 }}
              disabled={isLoading}
            >
              {isLoading ? 'Logging in...' : 'Login'}
            </Button>
            <Box mt={2} display="flex" justifyContent="space-between">
              <Link href="/register" variant="body2">
                Register with Vendor ID
              </Link>
              <Link href="/forgot-password" variant="body2">
                Forgot Password?
              </Link>
            </Box>
            <Box mt={2} textAlign="center">
              <Typography variant="body2">
                Want to run calls with us?{' '}
                <Link href="/applicationform" variant="body2" sx={{ fontWeight: 'bold' }}>
                  Become a Provider
                </Link>
              </Typography>
            </Box>
          </form>
          )}
        </CardContent>
      </Card>
      
      {/* Change Password Dialog */}
      <ChangePasswordDialog
        open={showChangePasswordDialog}
        onClose={() => setShowChangePasswordDialog(false)}
        onSuccess={handlePasswordChangeSuccess}
      />
    </Container>
  );
};

export default Login;
